/**
 * Browser-session scope for local risk caches.
 *
 * Access-token claims are authoritative when present. Stored profile/user
 * records may fill missing fields only when they do not conflict with the
 * active token, which prevents a stale profile from selecting another
 * tenant's cache during a session transition.
 */

export const SESSION_SCOPE_CHANGED_EVENT = 'aviato_session_scope_changed';

const scopeChangeListeners = new Set();

function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function safeJsonParse(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readStoredObject(storage, key) {
  try {
    return safeJsonParse(storage?.getItem(key));
  } catch {
    return null;
  }
}

function decodeJwtPayload(accessToken) {
  const token = String(accessToken || '').trim();
  const encodedPayload = token.split('.')[1];
  if (!encodedPayload || typeof globalThis.atob !== 'function') return null;
  try {
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return safeJsonParse(globalThis.atob(padded));
  } catch {
    return null;
  }
}

function firstIdentifier(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return '';
}

function userIdFrom(source) {
  return firstIdentifier(source?.user_id, source?.userId, source?.id, source?.sub);
}

function companyIdFrom(source) {
  return firstIdentifier(source?.company_id, source?.companyId, source?.company?.id);
}

function tenantSchemaFrom(source) {
  return firstIdentifier(
    source?.tenant_schema,
    source?.tenantSchema,
    source?.schema_name,
    source?.schemaName,
  );
}

function sourceMatchesToken(source, tokenClaims) {
  if (!source || typeof source !== 'object') return false;
  if (!tokenClaims) return true;

  const tokenUserId = userIdFrom(tokenClaims);
  const sourceUserId = userIdFrom(source);
  if (tokenUserId && sourceUserId && tokenUserId !== sourceUserId) return false;

  const tokenCompanyId = companyIdFrom(tokenClaims);
  const sourceCompanyId = companyIdFrom(source);
  if (tokenCompanyId && sourceCompanyId && tokenCompanyId !== sourceCompanyId) return false;

  const tokenTenantSchema = tenantSchemaFrom(tokenClaims);
  const sourceTenantSchema = tenantSchemaFrom(source);
  return !(tokenTenantSchema && sourceTenantSchema && tokenTenantSchema !== sourceTenantSchema);
}

/**
 * Derive the current company/tenant and user scope.
 *
 * Callers may pass explicit values for pure tests. Missing values are read
 * from user_profile, user and access_token in localStorage.
 */
export function deriveSessionScope(options = {}) {
  const storage = options.storage === undefined ? safeStorage() : options.storage;
  const profile = options.profile === undefined
    ? readStoredObject(storage, 'user_profile')
    : options.profile;
  const storedUser = options.user === undefined
    ? readStoredObject(storage, 'user')
    : options.user;
  const accessToken = options.accessToken === undefined
    ? (() => {
      try {
        return storage?.getItem('access_token') || '';
      } catch {
        return '';
      }
    })()
    : options.accessToken;
  const tokenClaims = options.tokenClaims === undefined
    ? decodeJwtPayload(accessToken)
    : options.tokenClaims;

  if (String(accessToken || '').trim() && !tokenClaims) {
    return {
      companyId: '',
      tenantSchema: '',
      companyScopeId: '',
      userId: '',
      isScoped: false,
    };
  }

  const trustedProfile = sourceMatchesToken(profile, tokenClaims) ? profile : null;
  const trustedUser = sourceMatchesToken(storedUser, tokenClaims) ? storedUser : null;
  const sources = [tokenClaims, trustedProfile, trustedUser].filter(Boolean);

  const companyId = firstIdentifier(...sources.map(companyIdFrom));
  const tenantSchema = firstIdentifier(...sources.map(tenantSchemaFrom));
  const userId = firstIdentifier(...sources.map(userIdFrom));
  const companyScopeId = companyId || tenantSchema;

  return {
    companyId,
    tenantSchema,
    companyScopeId,
    userId,
    isScoped: Boolean(companyScopeId && userId),
  };
}

export function buildSessionScopedStorageKey(prefix, scope = deriveSessionScope()) {
  const normalizedPrefix = String(prefix || '').trim().replace(/:+$/, '');
  const tenantSchema = firstIdentifier(scope?.tenantSchema);
  const companyId = firstIdentifier(scope?.companyId);
  const companyScopeId = firstIdentifier(scope?.companyScopeId);
  const userId = firstIdentifier(scope?.userId);
  if (!normalizedPrefix || !scope?.isScoped || !companyScopeId || !userId) return null;

  // PostgreSQL IDs restart inside each tenant schema. Keep the schema and
  // company as independent key segments so tenant_alpha/company=1/user=1 can
  // never collide with tenant_bravo/company=1/user=1 in browser storage.
  // Preserve the established company/user shape for legacy profiles that
  // genuinely have no tenant schema available.
  const identityParts = tenantSchema
    ? [tenantSchema, ...(companyId ? [companyId] : []), userId]
    : [companyScopeId, userId];
  return [normalizedPrefix, ...identityParts.map(encodeURIComponent)].join(':');
}

/**
 * Stable identity for React session boundaries.
 *
 * Keep tenant schema and company id as independent fields. During tenant
 * provisioning or account switching either value may change before the
 * other, and collapsing them into `companyScopeId` would let protected UI
 * state survive that identity transition.
 */
export function buildSessionIdentityKey(scope = deriveSessionScope()) {
  const tenantSchema = firstIdentifier(scope?.tenantSchema);
  const companyId = firstIdentifier(scope?.companyId);
  const userId = firstIdentifier(scope?.userId);

  return JSON.stringify([tenantSchema, companyId, userId]);
}

export function subscribeToSessionScopeChanges(listener) {
  if (typeof listener !== 'function') return () => {};
  scopeChangeListeners.add(listener);
  return () => scopeChangeListeners.delete(listener);
}

export function notifySessionScopeChanged() {
  for (const listener of scopeChangeListeners) {
    try {
      listener(deriveSessionScope());
    } catch {
      // Scope refresh is best-effort; one listener must not block the rest.
    }
  }
  try {
    window.dispatchEvent(new CustomEvent(SESSION_SCOPE_CHANGED_EVENT));
  } catch {
    // Non-browser callers do not need the DOM event.
  }
}

function isScopedRiskCacheKey(key) {
  return (
    /^aviato_hazard_register(?:_deleted)?_v2:/.test(key)
    || /^aviato_pdca(?:_cycles)?_v2:/.test(key)
    || /^aviato_hazard_analysis_v2:/.test(key)
    || /^aviato_frat_hazard_matrix_v2:/.test(key)
    || /^aviato_hfacs_draft_v1:/.test(key)
  );
}

function isAnyRiskCacheKey(key) {
  return (
    key.startsWith('aviato_hazard_register')
    || key.startsWith('aviato_pdca')
    || key.startsWith('aviato_hazard_analysis')
    || key.startsWith('aviato_frat_hazard_matrix')
    || key.startsWith('aviato_hfacs_draft')
  );
}

/**
 * Remove browser-only risk data.
 *
 * `includeLegacy: false` is used during an auth session replacement so the
 * old scoped v2 data is purged while the explicit v1 adoption path remains
 * available. The public default also removes all legacy keys.
 */
export function clearRiskLocalCaches({ includeLegacy = true, storage = safeStorage() } = {}) {
  if (!storage) return [];
  const keys = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key) keys.push(key);
    }
  } catch {
    return [];
  }

  const removed = [];
  for (const key of keys) {
    if (!(includeLegacy ? isAnyRiskCacheKey(key) : isScopedRiskCacheKey(key))) continue;
    try {
      storage.removeItem(key);
      removed.push(key);
    } catch {
      // Continue clearing the remaining keys.
    }
  }
  return removed;
}
